import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { SiteContent } from "@etheria/shared";
import {
  Activity,
  ArrowRight,
  BookOpen,
  CalendarDays,
  Check,
  Copy,
  Map,
  Server,
  Shield,
  Swords,
} from "lucide-react";
import { api } from "../lib/api";
import { useEventsCalendar } from "../api/events";
import { useSiteSettings } from "../api/settings";
import { filterContent, pickContent, useOnline, useServerStatus, useSiteContent } from "../lib/publicHooks";
import { Button, useToast } from "../components/ui";
import { MapPreview } from "../components/map/MapPreview";
import { HomeStyles } from "./home/HomeStyles";

const FALLBACK_IP = "play.ether-ia.fr:25575";

interface Chronicle {
  id: string;
  title: string;
  excerpt: string;
  publishedAt: number;
}

const CITY_PLACES = [
  { name: "La Taverne du Seuil", text: "Guildes, alliances et nouvelles du royaume.", icon: "barrel_side.png", to: "/guildes" },
  { name: "La Grande Forge", text: "Métiers, artisanat, gemmes et équipements.", icon: "anvil.png", to: "/wiki/metiers" },
  { name: "La Porte des Profondeurs", text: "Donjons, groupes et records de saison.", icon: "chiseled_stone_bricks.png", to: "/wiki/donjons" },
  { name: "Les Archives Vivantes", text: "Chroniques écrites par les actes des joueurs.", icon: "written_book.png", to: "/monde" },
] as const;

function useChronicles() {
  return useQuery({
    queryKey: ["conscience", "chronicles", "home-forge"],
    queryFn: ({ signal }) => api.get<{ chronicles: Chronicle[] }>("/conscience/chronicles", { params: { limit: 3 }, signal }),
    staleTime: 120_000,
    retry: false,
  });
}

export function Home() {
  const content = useSiteContent();
  const hero = pickContent(content.data, "hero");
  const announcements = filterContent(content.data, "announcement");

  return (
    <main className="etheria-home content-layer">
      <HomeStyles />
      <Hero hero={hero} />
      <WeeklyRegister />
      <LivingCity />
      {announcements.length > 0 && <News announcements={announcements} />}
      <WorldLedger />
      <Threshold />
    </main>
  );
}

function Hero({ hero }: { hero?: SiteContent }) {
  const settings = useSiteSettings();
  const online = useOnline();
  const status = useServerStatus();
  const toast = useToast();
  const [copied, setCopied] = useState(false);
  const ip = settings.data?.serverIp?.trim() || FALLBACK_IP;
  const title = hero?.title?.trim() || "Etheria";
  const body = hero?.body?.trim() || "Une forge vivante. Des alliances, des batailles, des économies et des histoires écrites par vous.";
  const tps = status.data?.perf.tps[0];

  const copyIp = async () => {
    try {
      await navigator.clipboard.writeText(ip);
      setCopied(true);
      toast.success("Adresse copiée", ip);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error("Copie impossible", ip);
    }
  };

  return (
    <section className="forge-hero" aria-labelledby="etheria-title">
      <div className="forge-hero__copy">
        <div className="forge-hero__brandline">
          <img src="/assets/brand/etheria-forge-crest.png" alt="" />
          <span>La conscience de Jeawyn</span>
        </div>
        <h1 id="etheria-title">{title}</h1>
        <div className="forge-hero__rule" aria-hidden><span /><img src="/assets/brand/etheria-forge-crest.png" alt="" /><span /></div>
        <p className="forge-hero__kicker">La conscience de Jeawyn</p>
        <p className="forge-hero__lead">{body}</p>

        <div className="forge-hero__telemetry" aria-label="État du royaume">
          <div><Activity className="h-4 w-4" /><strong>{online.data?.online ?? "-"}</strong><span>/ {online.data?.max ?? 300} joueurs en ligne</span></div>
          <div><Server className="h-4 w-4" /><span>TPS</span><strong>{tps == null ? "-" : tps.toFixed(2)}</strong></div>
        </div>

        <div className="forge-hero__actions">
          <button type="button" className="forge-join" onClick={copyIp}>
            <Swords className="h-5 w-5" />
            <span><strong>Rejoindre le royaume</strong><small>{ip}</small></span>
            {copied ? <Check className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
          </button>
          <Link to="/guide" className="forge-guide"><BookOpen className="h-5 w-5" /><span>Démarrer le guide</span><ArrowRight className="h-4 w-4" /></Link>
        </div>
        <p className="forge-hero__version">Version {status.data?.info.version ?? "26.1.2"} · Java & Bedrock</p>
      </div>

      <div className="forge-hero__media">
        <img src="/assets/brand/etheria-forge-gate.png" alt="La grande porte médiévale du royaume d'Etheria" />
        <span className="forge-hero__banner">E</span>
        <a href="#cette-semaine" className="forge-hero__scroll"><span>Défiler</span><ArrowRight className="h-4 w-4 rotate-90" /></a>
      </div>
    </section>
  );
}

function WeeklyRegister() {
  const calendar = useEventsCalendar();
  const days = calendar.data?.week ?? [];
  const current = calendar.data?.current;
  const visibleDays = (days.length ? days : fallbackWeek()).slice(0, 4);

  return (
    <section id="cette-semaine" className="forge-register">
      <aside className="forge-notice">
        <img src="/assets/brand/etheria-forge-crest.png" alt="" />
        <div>
          <p>Avis du royaume</p>
          <h2>Soyez un pilier d'Etheria.</h2>
          <span>Respect, entraide et ambition forgent notre avenir.</span>
        </div>
      </aside>

      <div className="forge-calendar">
        <header>
          <div><CalendarDays className="h-5 w-5" /><h2>Événements de la semaine</h2></div>
          <Link to="/evenements">Calendrier complet <ArrowRight className="h-4 w-4" /></Link>
        </header>
        {calendar.isError ? (
          <p className="forge-calendar__state">Le registre se synchronise avec le serveur.</p>
        ) : (
          <div className="forge-calendar__rows">
            {visibleDays.map((day) => (
              <article key={day.day} className={current?.day === day.day ? "is-active" : ""}>
                <time>{day.dayLabel || day.day}</time>
                <span>{day.eventTime || "Toute la journée"}</span>
                <Swords className="h-4 w-4" />
                <strong>{day.title}</strong>
                {day.xpMultiplier > 1 && <em>XP ×{day.xpMultiplier}</em>}
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function LivingCity() {
  return (
    <section className="home-section home-city">
      <SectionHeading eyebrow="La capitale" title="Le royaume a désormais un cœur" link="/carte" linkLabel="Ouvrir la carte" />
      <div className="home-city__grid">
        {CITY_PLACES.map((place, index) => (
          <Link key={place.name} to={place.to} className="home-city__place">
            <span>0{index + 1}</span>
            <img src={`/vanilla-items/${place.icon}`} alt="" />
            <div><h3>{place.name}</h3><p>{place.text}</p></div>
            <ArrowRight className="h-4 w-4" />
          </Link>
        ))}
      </div>
      <p className="home-city__note">
        Les nouveaux aventuriers entrent par le chemin du tutoriel, rencontrent les gardiens du Seuil puis découvrent les métiers, les guildes, les donjons et l'économie avant leur premier départ.
      </p>
    </section>
  );
}

function News({ announcements }: { announcements: SiteContent[] }) {
  return (
    <section className="home-section home-news">
      <SectionHeading eyebrow="Actualités" title="Les affiches du conseil" link="/patchnotes" linkLabel="Toutes les nouvelles" />
      <div className="home-news__grid">
        {announcements.slice(0, 3).map((item) => (
          <article key={item.key}><time>{new Date(item.updatedAt).toLocaleDateString("fr-FR")}</time><h3>{item.title}</h3><p>{item.body}</p></article>
        ))}
      </div>
    </section>
  );
}

function WorldLedger() {
  const chronicles = useChronicles();
  return (
    <section className="home-section home-ledger">
      <div className="home-ledger__chronicles">
        <SectionHeading eyebrow="Le livre du monde" title="Ce que la Conscience a retenu" link="/monde" linkLabel="Toutes les chroniques" />
        <div className="home-chronicle-list">
          {(chronicles.data?.chronicles ?? []).map((entry, index) => (
            <article key={entry.id}><span>{String(index + 1).padStart(2, "0")}</span><div><time>{new Date(entry.publishedAt).toLocaleDateString("fr-FR")}</time><h3>{entry.title}</h3><p>{entry.excerpt}</p></div></article>
          ))}
          {!chronicles.isLoading && (chronicles.data?.chronicles.length ?? 0) === 0 && <p className="home-inline-state">La prochaine page sera scellée à 20h à partir des actes du jour.</p>}
        </div>
      </div>
      <Link to="/carte" className="home-map-link" aria-label="Ouvrir la carte vivante">
        <MapPreview />
        <span><Map className="h-4 w-4" /><small>Cartographie en direct</small><strong>Voir où vit le royaume</strong></span>
      </Link>
    </section>
  );
}

function Threshold() {
  return (
    <section className="home-threshold">
      <div><Shield className="h-8 w-8" /><p>Ton histoire commence au Seuil</p><h2>Entre dans Etheria. Le royaume se souviendra de toi.</h2></div>
      <div><Link to="/guide"><Button size="lg">Préparer mon arrivée</Button></Link><Link to="/inscription"><Button size="lg" variant="danger">Créer mon compte</Button></Link></div>
    </section>
  );
}

function SectionHeading({ eyebrow, title, link, linkLabel }: { eyebrow: string; title: string; link: string; linkLabel: string }) {
  return (
    <header className="home-heading">
      <div><p>{eyebrow}</p><h2>{title}</h2></div>
      <Link to={link}>{linkLabel}<ArrowRight className="h-4 w-4" /></Link>
    </header>
  );
}

function fallbackWeek() {
  return [
    { day: "thursday", dayLabel: "Jeudi 16 juillet", title: "Soirée Donjons", description: "", category: "donjon", xpMultiplier: 1, job: "", event: "", eventTime: "20:00" },
    { day: "friday", dayLabel: "Vendredi 17 juillet", title: "Guerre des Guildes", description: "", category: "guildes", xpMultiplier: 1, job: "", event: "", eventTime: "20:30" },
    { day: "saturday", dayLabel: "Samedi 18 juillet", title: "Chasse au Trésor", description: "", category: "communaute", xpMultiplier: 1, job: "", event: "", eventTime: "15:00" },
    { day: "sunday", dayLabel: "Dimanche 19 juillet", title: "Jugement de la Conscience", description: "", category: "conscience", xpMultiplier: 1, job: "", event: "", eventTime: "20:00" },
  ] as import("../api/events").EventProgram[];
}
